import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import gsap from 'gsap'

import { easeLoad, prefersReducedMotion } from '@/lib/motion'

const STORAGE_KEY = 'metric-cookie-choice'

export const CookieNotice: FC = (): ReactNode => {
    const [visible, setVisible] = useState(false)
    const cardRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        let saved: string | null = null

        try {
            saved = window.localStorage.getItem(STORAGE_KEY)
        } catch {
            saved = null
        }

        if (!saved) {
            const timer = window.setTimeout(() => setVisible(true), 1400)

            return () => window.clearTimeout(timer)
        }
    }, [])

    useEffect(() => {
        const card = cardRef.current

        if (!visible || !card || prefersReducedMotion()) return

        const tween = gsap.fromTo(card, { y: 40, opacity: 0.001 }, {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: easeLoad
        })

        return () => {
            tween.kill()
        }
    }, [visible])

    const choose = (value: 'accepted' | 'declined'): void => {
        try {
            window.localStorage.setItem(STORAGE_KEY, value)
        } catch {
            /* storage blocked, the choice simply is not remembered */
        }

        const card = cardRef.current

        if (!card || prefersReducedMotion()) {
            setVisible(false)

            return
        }

        gsap.to(card, {
            y: 24,
            opacity: 0,
            duration: 0.4,
            ease: 'power2.in',
            onComplete: () => setVisible(false)
        })
    }

    if (!visible) return null

    return (
        <div
            ref={cardRef}
            role='dialog'
            aria-label='Cookie settings'
            className='fixed bottom-4 left-4 right-4 z-[100] max-w-[380px] rounded-[9px] border border-line bg-surface p-5 shadow-[0_18px_40px_rgba(0,0,0,0.45)] tab:bottom-6 tab:left-6 tab:right-auto'
        >
            <h2 className='font-sans text-[16px] font-medium leading-[1.3em] text-ink'>
                Cookie settings
            </h2>

            <p className='mt-2 font-body text-[14px] leading-[1.45em] text-ink-soft'>
                We use cookies to enhance your experience, analyze site traffic and deliver personalized content. Read our{' '}
                <Link href='/privacy#cookies' className='text-ink underline underline-offset-4 hover:text-white'>
                    Cookie Policy
                </Link>
                .
            </p>

            <div className='mt-4 flex items-center gap-2'>
                <button
                    type='button'
                    onClick={() => choose('accepted')}
                    className='h-[36px] flex-1 bg-[#fefff5] px-4 font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-[#0d0d0d] transition-opacity duration-300 hover:opacity-90'
                >
                    Accept
                </button>

                <button
                    type='button'
                    onClick={() => choose('declined')}
                    className='h-[36px] flex-1 border border-[#454545] px-4 font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink transition-colors duration-300 hover:border-[#fefff5]'
                >
                    Decline
                </button>
            </div>
        </div>
    )
}
